import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { UserProfile } from '../../types';
import { FadeInImage } from '../common/FadeInImage';

interface MentionInputProps {
  value: string;
  onChange: (val: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  selectedCity?: string;
  maxLength?: number;
  isTextArea?: boolean;
  id?: string;
  dropdownPosition?: 'top' | 'bottom';
}

export function MentionInput({
  value, onChange, onKeyDown, placeholder, className, autoFocus, selectedCity, maxLength, isTextArea, id, dropdownPosition = 'top'
}: MentionInputProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<UserProfile[]>([]);
  const [cursorPos, setCursorPos] = useState<number>(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!showDropdown) return;
      try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef);
        
        const snapshot = await getDocs(q);
        let users = snapshot.docs.map(d => ({ uid: d.id, ...d.data() } as UserProfile));
        
        // Include players and clubs
        users = users.filter(u => ['user', 'admin', 'superadmin'].includes(u.role));
        
        if (selectedCity && selectedCity !== 'Todas las ciudades') {
          users = users.filter(u => u.city === selectedCity);
        }
        
        const lowerQ = searchQuery.toLowerCase();
        const filtered = users.filter(u => u.name?.toLowerCase().includes(lowerQ)).slice(0, 10);
        setFilteredUsers(filtered);
        setActiveIndex(0);
      } catch (err) {
        console.error('Error fetching users for mention:', err);
      }
    };
    
    const timeout = setTimeout(fetchUsers, 300);
    return () => clearTimeout(timeout);
  }, [showDropdown, searchQuery, selectedCity]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const val = e.target.value;
    onChange(val);
    
    const pos = e.target.selectionStart || 0;
    setCursorPos(pos);
    
    const textBefore = val.slice(0, pos);
    const match = textBefore.match(/@([a-zA-Z0-9_\-\.]*)$/);
    if (match) {
      setSearchQuery(match[1]);
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  const insertMention = (user: UserProfile) => {
    const textBefore = value.slice(0, cursorPos);
    const textAfter = value.slice(cursorPos);
    
    const match = textBefore.match(/@([a-zA-Z0-9_\-\.]*)$/);
    if (match) {
      const formattedName = user.name?.replace(/\s+/g, '_');
      const mentionText = `@${formattedName} `;
      const newTextBefore = textBefore.slice(0, match.index) + mentionText;
      const newValue = newTextBefore + textAfter;
      onChange(newValue);
      setShowDropdown(false);
      
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          const newPos = newTextBefore.length;
          inputRef.current.setSelectionRange(newPos, newPos);
        }
      }, 10);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (showDropdown && filteredUsers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(prev => (prev + 1) % filteredUsers.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(prev => (prev - 1 + filteredUsers.length) % filteredUsers.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        insertMention(filteredUsers[activeIndex]);
      } else if (e.key === 'Escape') {
        setShowDropdown(false);
      } else if (onKeyDown) {
        onKeyDown(e);
      }
    } else if (onKeyDown) {
      onKeyDown(e);
    }
  };

  const commonProps = {
    id,
    ref: inputRef as any,
    value,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    placeholder,
    className,
    maxLength,
  };

  return (
    <div className="relative w-full">
      {isTextArea ? (
        <textarea {...commonProps} />
      ) : (
        <input type="text" {...commonProps} />
      )}
      
      {showDropdown && filteredUsers.length > 0 && (
        <div className={`absolute left-0 right-0 z-50 bg-slate-800 border border-white/10 rounded-xl shadow-xl overflow-hidden max-h-48 overflow-y-auto ${
          dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
        }`}>
          {filteredUsers.map((user, idx) => (
            <div
              key={user.uid}
              onClick={() => insertMention(user)}
              className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${idx === activeIndex ? 'bg-white/10' : 'hover:bg-white/5'}`}
            >
              <div className="w-6 h-6 rounded-full overflow-hidden bg-slate-700 shrink-0">
                {user.photoUrl ? (
                  <FadeInImage src={user.photoUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-white/50">
                    {user.name?.charAt(0)}
                  </div>
                )}
              </div>
              <span className="text-xs font-bold text-white/90">{user.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
